// src/components/layout/MultiTenantSider.tsx

import React, { useMemo } from 'react';
import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import styles from './MultiTenantSider.module.css';
import {
  DashboardOutlined,
  TeamOutlined,
  CreditCardOutlined,
  PayCircleOutlined,
  CalendarOutlined,
  BarChartOutlined,
  SettingOutlined,
  UserSwitchOutlined,
  BankOutlined,
  GlobalOutlined,
  GroupOutlined,
  HeartOutlined,
  PlusOutlined,
  FundOutlined,
  MoneyCollectOutlined,
} from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

// Helper function to create menu items with proper typing and accessibility
const createMenuItem = (
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group',
): MenuItem => ({
  key,
  icon,
  children,
  label,
  type,
} as MenuItem);

interface MultiTenantSiderProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const MultiTenantSider: React.FC<MultiTenantSiderProps> = ({ collapsed, onCollapse }) => {
  const location = useLocation();
  const { isSuperAdmin, isCommunalAdmin } = useAuthContext();

  // Improved route matching for better menu selection
  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    const keys: string[] = [];

    // Exact matches
    const exactPaths = [
      '/dashboard',
      '/dashboard/system',
      '/members',
      '/credits',
      '/payments',
      '/meetings',
      '/reports',
      '/settings',
      '/staff',
      '/activities',
      '/expenses',
      '/contributions'
    ];

    if (exactPaths.includes(path)) {
      keys.push(path);
    }
    
    // Handle nested routes
    if (path.startsWith('/regions')) return ['/regions'];
    if (path.startsWith('/provinces')) return ['/provinces'];
    if (path.startsWith('/villages')) return ['/villages'];
    if (path.startsWith('/groups')) return ['/groups'];
    if (path.startsWith('/communes')) return ['/communes'];
    if (path.startsWith('/solidarity/contributions')) return ['/solidarity/contributions'];
    if (path.startsWith('/solidarity/requests')) return ['/solidarity'];
    if (path.startsWith('/members/approvals')) return ['/members/approvals'];
    if (path.startsWith('/reports/country')) return ['/reports/country'];
    if (path.startsWith('/savings')) return ['/contributions']; // Redirect savings to contributions

    return keys.length > 0 ? keys : [path];
  }, [location.pathname]);

  // Track open submenus
  const openKeys = useMemo(() => {
    const path = location.pathname;
    const keys: string[] = [];

    if (path.startsWith('/solidarity')) keys.push('financial-services');
    if (path.startsWith('/members')) keys.push('member-management');
    if (path.match(/^\/(activities|expenses|contributions)/)) {
      keys.push('group-activities');
    }

    return keys;
  }, [location.pathname]);

  // Super Admin Navigation
  const getSuperAdminMenuItems = (): MenuItem[] => [
    createMenuItem(
      <Link to="/dashboard/system" aria-label="Tableau de bord système">Tableau de bord système</Link>,
      '/dashboard/system',
      <GlobalOutlined />
    ),
    createMenuItem(
      'Gestion du système',
      'system-management',
      <SettingOutlined />,
      [
        createMenuItem(
          <Link to="/regions" aria-label="Gestion des régions">Gestion des régions</Link>,
          '/regions',
          <GlobalOutlined />
        ),
        createMenuItem(
          <Link to="/provinces" aria-label="Gestion des provinces">Gestion des provinces</Link>,
          '/provinces',
          <GlobalOutlined />
        ),
        createMenuItem(
          <Link to="/communes" aria-label="Gestion des communes">Gestion des communes</Link>,
          '/communes',
          <GlobalOutlined />
        ),
        createMenuItem(
          <Link to="/villages" aria-label="Gestion des villages">Gestion des villages</Link>,
          '/villages',
          <BankOutlined />
        ),
        createMenuItem(
          <Link to="/staff" aria-label="Gestion du personnel">Gestion du personnel</Link>,
          '/staff',
          <UserSwitchOutlined />
        ),
        createMenuItem(
          <Link to="/settings" aria-label="Paramètres du système">Paramètres du système</Link>,
          '/settings',
          <SettingOutlined />
        ),
      ]
    ),
    createMenuItem(
      <Link to="/groups" aria-label="Tous les groupes">Tous les groupes</Link>,
      '/groups',
      <GroupOutlined />
    ),
    createMenuItem(
      <Link to="/reports/country" aria-label="Rapports nationaux">Rapports</Link>,
      '/reports/country',
      <BarChartOutlined />
    )
  ];

  // Communal Admin Navigation
  const getCommunalAdminMenuItems = (): MenuItem[] => [
    createMenuItem(
      <Link to="/dashboard" aria-label="Tableau de bord de la commune">Tableau de bord de la commune</Link>,
      '/dashboard',
      <DashboardOutlined />
    ),
    createMenuItem(
      <Link to="/groups" aria-label="Mes groupes">Mes groupes</Link>,
      '/groups',
      <GroupOutlined />
    ),
    createMenuItem(
      'Gestion des membres',
      'member-management',
      <TeamOutlined />,
      [
        createMenuItem(
          <Link to="/members" aria-label="Tous les membres">Tous les membres</Link>,
          '/members',
          <TeamOutlined />
        ),
        createMenuItem(
          <Link to="/members/approvals" aria-label="Approbations d'adhésion">Approbations d'adhésion</Link>,
          '/members/approvals',
          <UserSwitchOutlined />
        ),
      ]
    ),
    createMenuItem(
      'Services financiers',
      'financial-services',
      <BankOutlined />,
      [
        createMenuItem(
          <Link to="/contributions" aria-label="Contributions hebdomadaires">Contributions hebdomadaires</Link>,
          '/contributions',
          <CalendarOutlined />
        ),
        createMenuItem(
          <Link to="/credits" aria-label="Crédits">Crédits</Link>,
          '/credits',
          <CreditCardOutlined />
        ),
        createMenuItem(
          'Solidarité',
          'solidarity-menu',
          <HeartOutlined />,
          [
            createMenuItem(
              <Link to="/solidarity" aria-label="Contributions de solidarité">Contributions</Link>,
              '/solidarity/contributions',
              <HeartOutlined />
            ),
            createMenuItem(
              <Link to="/solidarity/requests" aria-label="Demandes de solidarité">Demandes</Link>,
              '/solidarity',
              <HeartOutlined />
            ),
          ]
        ),
        createMenuItem(
          <Link to="/payments" aria-label="Paiements">Paiements</Link>,
          '/payments',
          <PayCircleOutlined />
        ),
      ]
    ),
    createMenuItem(
      'Activités de groupe',
      'group-activities',
      <FundOutlined />,
      [
        createMenuItem(
          <Link to="/activities" aria-label="Activités de revenus">AGR</Link>,
          '/activities',
          <PlusOutlined />
        ),
        createMenuItem(
          <Link to="/expenses" aria-label="Dépenses de groupe">Dépenses de groupe</Link>,
          '/expenses',
          <MoneyCollectOutlined />
        ),
      ]
    ),
    createMenuItem(
      <Link to="/meetings" aria-label="Réunions">Réunions</Link>,
      '/meetings',
      <CalendarOutlined />
    ),
    createMenuItem(
      <Link to="/reports" aria-label="Rapports de la commune">Rapports de la commune</Link>,
      '/reports',
      <BarChartOutlined />
    )
  ];

  // Group Agent Navigation - REMOVED (Group agents use mobile app)

  // Memoize menu items based on user role
  const menuItems = useMemo(() => {
    if (isSuperAdmin()) return getSuperAdminMenuItems();
    if (isCommunalAdmin()) return getCommunalAdminMenuItems();
    // Group agents use mobile app, no admin dashboard access
    return [];
  }, [isSuperAdmin, isCommunalAdmin]);


  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      width={280}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 10,
      }}
    >

      {/* Navigation Menu */}
      <Menu 
        theme="dark" 
        mode="inline" 
        selectedKeys={selectedKeys}
        defaultOpenKeys={openKeys}
        items={menuItems}
        className={`${styles.sidebarMenu} sidebar-menu`}
        style={{ 
          height: '100%', 
          borderRight: 0,
        }}
        triggerSubMenuAction="hover"
        role="navigation"
        aria-label="Main navigation"
        subMenuOpenDelay={0.2}
        subMenuCloseDelay={0.1}
        inlineIndent={12}
        overflowedIndicator={null}
      />
    </Sider>
  );
};

export default MultiTenantSider;